import styled, { css } from "styled-components";
import { FC } from "react";

import Text from "@/components/Text";
import { OptimisationTestStatusEnum } from "@/utils/enums";

export const Dot = styled.div<{
  $color: "green" | "yellow" | "red" | "blue";
  $withMargin?: boolean;
}>`
  width: 6px;
  height: 6px;
  display: inline-block;
  border-radius: 50%;

  ${({ theme, $color }) =>
    $color === "red" &&
    css`
      background: ${theme.colors.error};
    `}
  ${({ theme, $color }) =>
    $color === "green" &&
    css`
      background: ${theme.colors.success};
    `}
    ${({ theme, $color }) =>
    $color === "yellow" &&
    css`
      background: ${theme.colors.warning};
    `}
  ${({ $color }) =>
    $color === "blue" &&
    css`
      background: #3793e5;
    `}
  ${({ $withMargin }) =>
    $withMargin &&
    css`
      margin-right: 8px;
    `}
`;

export const Badge = styled.div<{
  $status?: OptimisationTestStatusEnum;
  $black?: boolean;
  $big?: boolean;
}>`
  display: inline-block;
  padding: 2px 6px;
  padding-left: 14px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 2px;
  ${Text} {
    font-weight: 700;
    font-size: 9px;
    line-height: 12px;
    position: relative;
    color: ${({ theme }) => theme.colors.gray};
    &:before {
      content: "";
      width: 4px;
      height: 4px;
      left: -8px;
      background-color: ${({ theme }) => theme.colors.gray};
      top: calc(50% - 2px);
      border-radius: 50%;
      position: absolute;
    }
  }
  ${({ $status }) =>
    $status === OptimisationTestStatusEnum.ENDED &&
    css`
      background: ${({ theme }) => theme.colors.successLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.success};
        &:before {
          background: ${({ theme }) => theme.colors.success};
        }
      }
    `}
  ${({ $status }) =>
    $status === OptimisationTestStatusEnum.FAILED &&
    css`
      background: ${({ theme }) => theme.colors.errorLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.error};
        &:before {
          background: ${({ theme }) => theme.colors.error};
        }
      }
    `}
  ${({ $status }) =>
    $status === OptimisationTestStatusEnum.WAITING_FOR_DATA &&
    css`
      background: ${({ theme }) => theme.colors.infoLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.info};
        &:before {
          background: ${({ theme }) => theme.colors.info};
        }
      }
    `}
  ${({ $status }) =>
    $status! >= OptimisationTestStatusEnum.PROCESSING &&
    $status! <= OptimisationTestStatusEnum.RUNNING &&
    css`
      background: ${({ theme }) => theme.colors.warningLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.warning};
        &:before {
          background: ${({ theme }) => theme.colors.warning};
        }
      }
    `}
    ${({ $status, $black }) =>
    !!$status &&
    $black &&
    css`
      ${Text} {
        color: ${({ theme }) => theme.colors.black};
      }
    `}
    ${({ $big }) =>
    $big &&
    css`
      ${Text} {
        font-size: 11px;
        line-height: 15px;
      }
    `}
`;

const renderBadgeTitle = (status?: OptimisationTestStatusEnum) => {
  switch (status) {
    case OptimisationTestStatusEnum.FAILED:
      return "Failed";
    case OptimisationTestStatusEnum.PROCESSING:
      return "Processing";
    case OptimisationTestStatusEnum.READY:
      return "Ready";
    case OptimisationTestStatusEnum.RUNNING:
      return "Running";
    case OptimisationTestStatusEnum.WAITING_FOR_DATA:
      return "Waiting";
    case OptimisationTestStatusEnum.ENDED:
      return "Completed";
    default:
      return "N/A";
  }
};

interface Props {
  status?: OptimisationTestStatusEnum;
  black?: boolean;
  big?: boolean;
  className?: string;
}

const OptimisationStatusBadge: FC<Props> = ({
  status,
  black = false,
  big = false,
  className,
}) => {
  return (
    <Badge className={className} $status={status} $big={big} $black={black}>
      <Text $type="smallMedium">{renderBadgeTitle(status)}</Text>
    </Badge>
  );
};

export default OptimisationStatusBadge;
